import { RequestCache } from "./RequestCache.ts";
import { isRemoteReceiver, remoteCall } from '../cast/RemoteSession.ts';
import { getSourcePolicy, assertSourceUrl } from "./SourceRegistry.ts";
import { TransportRouter } from "./TransportRouter.ts";
import {
  NetworkError,
  type HttpRequest,
  type HttpResponse,
  type ImageRequest,
  type YomiriHttpClient as ClientContract,
} from "./types.ts";

export interface HttpClientOptions {
  router: TransportRouter;
  sessionKey: () => string;
  textCache?: RequestCache<string>;
  bytesCache?: RequestCache<Uint8Array>;
}

export class YomiriHttpClient implements ClientContract {
  private readonly textCache: RequestCache<string>;
  private readonly bytesCache: RequestCache<Uint8Array>;

  constructor(private readonly options: HttpClientOptions) {
    this.textCache = options.textCache ?? new RequestCache<string>();
    this.bytesCache =
      options.bytesCache ?? new RequestCache<Uint8Array>({ maxBytes: 4 * 1024 * 1024 });
  }

  text(request: HttpRequest & { responseType: "text" }): Promise<HttpResponse<string>> {
    return this.cached(request, this.textCache, (shared) =>
      this.options.router.text({ ...shared, responseType: "text" }),
    );
  }

  bytes(request: HttpRequest & { responseType: "bytes" }): Promise<HttpResponse<Uint8Array>> {
    return this.cached(request, this.bytesCache, (shared) =>
      this.options.router.bytes({ ...shared, responseType: "bytes" }),
    );
  }

  prepareImage(request: ImageRequest) {
    if(isRemoteReceiver())return remoteCall<{base64:string;mime:string}>('media.image',{
      sourceId:request.sourceId,url:request.url,headers:request.headers,
    }).then(data=>{
      const bytes=Uint8Array.from(atob(data.base64),c=>c.charCodeAt(0));
      const url=URL.createObjectURL(new Blob([bytes],{type:data.mime}));
      return {url,delivery:'blob' as const,release(){URL.revokeObjectURL(url);}};
    });
    assertSourceUrl(request.sourceId, request.url);
    return this.options.router.prepareImage({
      ...request,
      sourceId: getSourcePolicy(request.sourceId).id,
    });
  }

  clearCache(): void {
    this.textCache.clear();
    this.bytesCache.clear();
  }

  private async cached<T extends string | Uint8Array>(
    request: HttpRequest,
    cache: RequestCache<T>,
    loader: (shared: Omit<HttpRequest, "signal">) => Promise<HttpResponse<T>>,
  ) {
    const source = getSourcePolicy(request.sourceId);
    assertSourceUrl(source.id, request.url);
    const sessionKey = this.options.sessionKey();
    const response = await cache.getOrLoad(
      { ...request, sourceId: source.id },
      { sourceRevision: source.revision, transport: "auto", sessionKey },
      loader,
    );
    if (sessionKey !== this.options.sessionKey())
      throw new NetworkError("ABORTED", "La sesión o la configuración cambió.");
    return response;
  }
}
